import React, { memo, useState } from 'react';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import {
  DeleteOutlined as DeleteOutlinedIcon,
  EditOutlined as EditOutlinedIcon,
} from '@material-ui/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import styles from './devices.styles';
import { Typography } from '../../components/Typography';
import { Card } from '../../components/Card';
import { Dropdown } from '../../components/Dropdown';
import DeleteDeviceModal from './components/deleteDeviceModal';
import { ResetDeviceModal, EditDeviceModal } from './components';
import { IRequestParams } from '../../components/types';
import { DeviceGrid } from '../../components/device-grid/device-grid';

interface IDevicesView extends WithStyles<typeof styles> {
  selectDevice: any;
  devicesList: any;
  permissions: any;
  handleDeleteDevice: (id: any) => void;
  handleEditDevice: (data: { id: string; name: string }) => void;
  handleResetDevice: (id: string) => void;
  handleGetDevices: ({ limit, offset, sortOrder }: IRequestParams) => void;
}

const DevicesView: React.FC<IDevicesView> = ({
  classes,
  selectDevice,
  devicesList,
  handleDeleteDevice,
  handleEditDevice,
  handleResetDevice,
}: IDevicesView) => {
  const [openDeleteDeviceModal, setOpenDeleteDeviceModal] = useState(false);
  const [openEditDeviceModal, setOpenEditDeviceModal] = useState(false);
  const [openResetDeviceModal, setOpenResetDeviceModal] = useState(false);
  const [openMoreOption, setOpenMoreOption] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const intl = useIntl();

  const DeviceOptionList = [
    {
      label: intl.formatMessage({
        id: 'unassign_this_device',
      }),
      id: 'device-unassign_this_device',
      onClick: () => setOpenDeleteDeviceModal(true),
      icon: <DeleteOutlinedIcon fontSize="small" />,
    },
    {
      label: intl.formatMessage({
        id: 'edit_this_device',
      }),
      id: 'device-edit_this_device',
      onClick: () => setOpenEditDeviceModal(true),
      icon: <EditOutlinedIcon fontSize="small" />,
    },
  ];

  const handleOpenMoreOption = (
    event: React.MouseEvent<HTMLElement>,
    data: any
  ) => {
    setSelectedDevice(data);
    setOpenMoreOption(!openMoreOption);
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMoreOption = (event: React.MouseEvent<EventTarget>) => {
    // setAnchorEl(null);
    setOpenMoreOption(false);
  };

  const handleOpenResetModal = (
    event: React.MouseEvent<HTMLElement>,
    data: any
  ) => {
    setSelectedDevice(data);
    setOpenResetDeviceModal(!openResetDeviceModal);
    setAnchorEl(event.currentTarget);
  };

  return (
    <div className={classes.root}>
      <div className={classes.topWrapper} id="devices-view">
        <Typography variant="h3" component="h2" color="textPrimary">
          <FormattedMessage id="devices" />
        </Typography>
      </div>
      <div className={classes.bottomWrapper}>
        <Card customStyles={classes.card}>
          <DeviceGrid
            onResetClick={handleOpenResetModal}
            onOpenMoreClick={handleOpenMoreOption}
            onDeleteDeviceClick={handleDeleteDevice}
            devices={devicesList.data}
            isLoading={selectDevice.loading}
            columnNames={[
              'name',
              'id',
              'tvAvailable',
              'PrivacyEnabled',
              'PrivacyTimeOut',
              'rebootStatus',
              'managedBy',
              'version',
              'reset',
              'more',
            ]}
            id="devices-list-table"
          />
        </Card>
      </div>
      <DeleteDeviceModal
        selectDevice={selectDevice}
        open={openDeleteDeviceModal}
        handleClose={() => setOpenDeleteDeviceModal(false)}
        selectedDevice={selectedDevice}
        handleDeleteDevice={handleDeleteDevice}
      />
      <EditDeviceModal
        selectDevice={selectDevice}
        open={openEditDeviceModal}
        handleClose={() => setOpenEditDeviceModal(false)}
        selectedDevice={selectedDevice}
        handleEditDevice={handleEditDevice}
      />
      <ResetDeviceModal
        selectDevice={selectDevice}
        open={openResetDeviceModal}
        handleClose={() => setOpenResetDeviceModal(false)}
        selectedDevice={selectedDevice}
        handleResetDevice={handleResetDevice}
      />
      <Dropdown
        id="device-more-option-button"
        open={Boolean(selectedDevice) && openMoreOption}
        anchorRef={anchorEl}
        handleClose={handleCloseMoreOption}
        itemList={DeviceOptionList}
      />
    </div>
  );
};

export default memo(withStyles(styles)(DevicesView));
